import { useCallback } from "react";
import {
  useBrand,
  useCanSpendCredits,
  useIsCompanyOwner,
} from "@/app/(components)/specifics/wrappers/BrandProvider/BrandProvider";
import { ModalStore } from "@/app/stores/ModalStore";
import { UserStore } from "@/app/stores/UserStore";
import { PageParams } from "@/app/types/common/DefaultProps";

// Central guard for credit-consuming actions: returns a function that checks whether the
// action is allowed and, when it is not, opens the BuyCredits modal and returns false.
// In a company, a non-owner who cannot spend (viewer) or ran out of shared credits is
// blocked from every credit action whatever the cost, so the informative popup opens
// straight away instead of the cost confirmation.
export function useCreditGuard(params: PageParams) {
  const balance = UserStore((state) => state.user?.processingBalance ?? 0);
  const isTenant = useBrand() !== null;
  const isCompanyOwner = useIsCompanyOwner();
  const canSpendCredits = useCanSpendCredits();

  return useCallback(
    (cost: number = 1): boolean => {
      const blockedInCompany = isTenant && !isCompanyOwner && (!canSpendCredits || balance <= 0);

      if (blockedInCompany || balance < cost) {
        ModalStore.getState().openBuyCredits(params);
        return false;
      }
      return true;
    },
    [balance, params, isTenant, isCompanyOwner, canSpendCredits],
  );
}
